import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Bookmark, BookmarkPlus, ChevronDown } from "lucide-react";
import { getUserToken } from "@/lib/userToken";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-channel plugin param slider. Pure presentation + on-change calls
 * `engine.setPluginParam`. iter-146 — extracted from PluginManagerPanel.jsx
 * along with PresetMenu since the two travel together.
 */
export function PluginParamSlider({ pluginId, channelIndex, paramSchema, currentValue, engine }) {
  const [val, setVal] = useState(currentValue ?? paramSchema.defaultValue);
  const onChange = (e) => {
    const v = Number(e.target.value);
    setVal(v);
    engine.setPluginParam(channelIndex, pluginId, paramSchema.key, v);
  };
  return (
    <label className="block">
      <span className="flex items-center justify-between font-mono text-[9.5px] uppercase tracking-wider text-white/55">
        <span>{paramSchema.label}</span>
        <span data-testid={`plugin-param-value-${pluginId}-${paramSchema.key}`} className="text-fuchsia-200">
          {Number.isInteger(paramSchema.step) ? val : val.toFixed(2)}
          {paramSchema.unit ? ` ${paramSchema.unit}` : ""}
        </span>
      </span>
      <input
        data-testid={`plugin-param-${pluginId}-${paramSchema.key}`}
        type="range"
        min={paramSchema.min}
        max={paramSchema.max}
        step={paramSchema.step}
        value={val}
        onChange={onChange}
        className="mt-1 w-full accent-cyan-400"
      />
    </label>
  );
}

/**
 * Preset dropdown for a single loaded plugin. Loads its preset list from
 * `/api/plugins/presets` (per-user), saves the engine's current state as
 * a named preset, and lets the user delete saved entries.
 *
 * iter-146 — extracted from PluginManagerPanel.jsx (was inline, ~170 lines).
 * State (presets/open/naming/name) stays here since it's preset-list local.
 */
export function PresetMenu({ entry, channelIndex, engine, pluginSlug, onLoaded }) {
  const [presets, setPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  const userToken = (typeof window !== "undefined") ? getUserToken() : "";

  const refresh = async () => {
    if (!pluginSlug) return;
    try {
      const url = `${API}/api/plugins/presets?plugin_id=${encodeURIComponent(pluginSlug)}&user_token=${encodeURIComponent(userToken)}`;
      const r = await fetch(url, { credentials: "include" });
      if (r.ok) setPresets(await r.json());
    } catch (e) { void e; }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [pluginSlug]);
  // Refresh on every open so we don't show stale data after a save.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) refresh(); }, [open]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name your preset first"); return; }
    const params = engine.getPluginState(channelIndex, entry.id) || {};
    try {
      const r = await fetch(`${API}/api/plugins/presets`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plugin_id: pluginSlug, name: trimmed, params, user_token: userToken }),
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const saved = await r.json();
      toast.success("Preset saved", { description: `"${saved.name}" — params snapshot stored.` });
      setNaming(false);
      setName("");
      // Optimistic upsert so the dropdown shows the new entry immediately.
      setPresets((prev) => {
        const filtered = prev.filter((p) => p.id !== saved.id);
        return [saved, ...filtered];
      });
      refresh();
    } catch (e) {
      toast.error("Could not save preset", { description: e.message?.slice(0, 100) });
    }
  };

  const handleLoad = (preset) => {
    engine.setPluginState(channelIndex, entry.id, preset.params);
    toast.message("Preset loaded", { description: `"${preset.name}" applied to ${entry.label}.` });
    setOpen(false);
    onLoaded?.(preset);
  };

  const handleDelete = async (preset, ev) => {
    ev.stopPropagation();
    try {
      const url = `${API}/api/plugins/presets/${encodeURIComponent(preset.id)}?user_token=${encodeURIComponent(userToken)}`;
      const r = await fetch(url, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      toast.message("Preset deleted");
      refresh();
    } catch (e) {
      toast.error("Could not delete preset", { description: e.message?.slice(0, 100) });
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`preset-menu-toggle-${entry.id}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md bg-fuchsia-500/[0.14] px-2 py-1 font-mono text-[9.5px] uppercase tracking-wider text-fuchsia-100 ring-1 ring-fuchsia-400/30 transition hover:bg-fuchsia-500/[0.24]"
      >
        <Bookmark className="h-3 w-3" />
        Presets {presets.length > 0 ? `· ${presets.length}` : ""}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div
          data-testid={`preset-menu-${entry.id}`}
          className="absolute right-0 top-full z-10 mt-1 w-64 rounded-xl border border-white/[0.1] bg-zinc-900/95 p-2 shadow-[0_12px_28px_-8px_rgba(0,0,0,0.65)] backdrop-blur"
        >
          {naming ? (
            <div className="space-y-2">
              <input
                data-testid={`preset-name-input-${entry.id}`}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                placeholder="Preset name"
                autoFocus
                className="w-full rounded-lg border border-white/10 bg-black/30 px-2 py-1.5 font-mono text-[11px] text-white placeholder:text-white/30 focus:border-fuchsia-400/40 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid={`preset-save-confirm-${entry.id}`}
                  onClick={handleSave}
                  className="flex-1 rounded-md bg-fuchsia-500/[0.22] py-1 font-mono text-[10px] uppercase tracking-wider text-fuchsia-100 ring-1 ring-fuchsia-400/40 hover:bg-fuchsia-500/[0.36]"
                >Save</button>
                <button
                  type="button"
                  onClick={() => { setNaming(false); setName(""); }}
                  className="flex-1 rounded-md bg-white/[0.06] py-1 font-mono text-[10px] uppercase tracking-wider text-white/65 hover:bg-white/[0.12]"
                >Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                data-testid={`preset-save-new-${entry.id}`}
                onClick={() => setNaming(true)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 font-mono text-[10.5px] uppercase tracking-wider text-fuchsia-200 transition hover:bg-fuchsia-500/[0.16]"
              >
                <BookmarkPlus className="h-3 w-3" />
                Save current state…
              </button>
              {presets.length > 0 && <div className="my-1 h-px bg-white/[0.06]" />}
              <div className="max-h-48 overflow-y-auto">
                {presets.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`preset-row-${p.id}`}
                    className="flex items-center justify-between rounded-md px-2 py-1 hover:bg-white/[0.04]"
                  >
                    <button
                      type="button"
                      data-testid={`preset-load-${p.id}`}
                      onClick={() => handleLoad(p)}
                      className="flex-1 truncate text-left font-mono text-[10.5px] text-white/80 hover:text-white"
                    >
                      {p.name}
                    </button>
                    <button
                      type="button"
                      data-testid={`preset-delete-${p.id}`}
                      onClick={(e) => handleDelete(p, e)}
                      className="grid h-5 w-5 place-items-center rounded text-rose-300/65 hover:bg-rose-500/15 hover:text-rose-200"
                      title="Delete preset"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {presets.length === 0 && (
                  <p className="px-2 py-2 font-mono text-[9.5px] uppercase tracking-wider text-white/35">
                    No saved presets yet
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
